import { initializeApp } from "firebase/app";
import { deleteToken, getMessaging, getToken, onMessage } from "firebase/messaging";
import { Preferences } from '@capacitor/preferences';
import { Subject } from 'rxjs';
import { environment } from '../environments/environment';

/** Emits whenever a foreground FCM message arrives. */
export const fcmForegroundMessage$ = new Subject<any>();
export const fcmTokenChanged$ = new Subject<string>();

const hasActiveAuthenticatedSession = async (): Promise<boolean> => {
  const { value } = await Preferences.get({ key: 'auth' });
  const token = (value || '').trim();
  return Boolean(token && token !== 'null' && token !== 'undefined');
};

export const getFcmMessageKey = (payload: any): string => {
  const title = payload?.notification?.title ?? payload?.data?.title ?? 'New Notification';
  const body = payload?.notification?.body ?? payload?.data?.body ?? '';
  const orderIdentifier = payload?.data?.orderId
    ?? payload?.data?.orderNo
    ?? payload?.data?.orderNumber
    ?? payload?.data?.orderReference
    ?? payload?.data?.orderReferenceId;

  if (orderIdentifier) {
    return `order:${String(orderIdentifier).trim()}`;
  }

  return `${String(title).trim()}|${String(body).trim()}`;
};

const cacheFcmToken = async (token: string): Promise<void> => {
  const normalizedToken = token.trim();

  if (!normalizedToken) {
    return;
  }

  const cachedToken = await Preferences.get({ key: 'fcm_token' });
  if (cachedToken.value === normalizedToken) {
    return;
  }

  await Preferences.set({ key: 'fcm_token', value: normalizedToken });
  fcmTokenChanged$.next(normalizedToken);
};

const app = initializeApp(environment.firebase);
export const messaging = getMessaging(app);

const configuredVapidKey = (
  (environment as any)?.firebase?.vapidKey
  ?? (environment as any)?.firebaseVapidKey
  ?? ''
).trim();

const isTokenSubscribeCredentialError = (error: unknown): boolean => {
  const code = String((error as any)?.code ?? '');
  const message = String((error as any)?.message ?? '').toLowerCase();

  return code === 'messaging/token-subscribe-failed'
    && (
      message.includes('missing required authentication credential')
      || message.includes('request is missing required authentication credential')
      || message.includes('unauthorized')
    );
};

const getTokenWithTimeout = async (vapidKey?: string): Promise<string | null> => {
  const tokenPromise = getToken(
    messaging,
    vapidKey ? { vapidKey } : {}
  );

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('Token request timeout')), 5000)
  );

  const token = await Promise.race([tokenPromise, timeoutPromise]);
  return token ?? null;
};

const getFcmTokenBestEffort = async (): Promise<string | null> => {
  if (!configuredVapidKey) {
    // Missing custom VAPID key is a valid setup because Firebase provides
    // a default web push key. Keep this path silent to avoid noisy consoles.
    return getTokenWithTimeout();
  }

  try {
    return await getTokenWithTimeout(configuredVapidKey);
  } catch (error) {
    if (!isTokenSubscribeCredentialError(error)) {
      throw error;
    }

    console.warn(
      `Configured Firebase VAPID key was rejected for project "${environment.firebase.projectId}". Retrying with Firebase default key.`,
      error
    );

    return getTokenWithTimeout();
  }
};

export const requestWebFCMToken = async (): Promise<string | null> => {
  try {
    // Check if Notification API is supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return null;
    }

    console.log('Requesting notification permission...');
    
    // Add timeout to prevent hanging
    const permissionPromise = Notification.requestPermission();
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Permission request timeout')), 5000)
    );
    
    const permission = await Promise.race([permissionPromise, timeoutPromise]).catch(err => {
      console.warn('Permission request failed or timed out:', err);
      return 'denied';
    });
    
    console.log('Notification permission:', permission);

    if (permission === "granted") {
      console.log('Requesting FCM token...');

      const token = await getFcmTokenBestEffort().catch(err => {
        console.warn('Token request failed or timed out:', err);
        return null;
      });

      if (token) {
        await cacheFcmToken(token);
        console.log("✅ Web FCM Token:", token);
        return token;
      } else {
        console.warn("⚠️ FCM Token is empty");
        return null;
      }
    } else {
      console.log("🔒 Notification Permission Denied or Dismissed");
      return null;
    }
  } catch (err) {
    console.error("❌ FCM Token Error:", err);
    console.error("Error details:", JSON.stringify(err, null, 2));
    return null;
  }
};

export const clearWebFCMStateOnLogout = async (): Promise<void> => {
  try {
    await Preferences.remove({ key: 'fcm_token' });
  } catch (error) {
    console.warn('Failed to clear cached FCM token from Preferences:', error);
  }

  try {
    await deleteToken(messaging);
  } catch (error) {
    console.warn('Failed to delete browser FCM token during logout:', error);
  }

  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      const notifications = await registration.getNotifications();
      notifications.forEach((notification) => notification.close());
    }
  } catch (error) {
    console.warn('Failed to clear visible push notifications during logout:', error);
  }

  fcmTokenChanged$.next('');
};

export const listenForMessages = () => {
  onMessage(messaging, async (payload) => {
    if (!(await hasActiveAuthenticatedSession())) {
      console.log('Ignoring foreground message because no authenticated session is active.');
      return;
    }

    console.log("Foreground Message:", payload);

    // Notify any subscribers (e.g. OrderHistoryComponent) about the new message.
    fcmForegroundMessage$.next(payload);

    // The service worker (push event handler) is responsible for showing the
    // system notification in ALL cases (foreground + background).  This avoids
    // a key-mismatch between the sound-start key and the notification's embedded
    // key that is sent back on close/click.
  });
};

/**
 * Periodically checks if the FCM token has changed and updates the cache.
 * Firebase can rotate tokens at any time; this ensures we always have the latest.
 */
export const startTokenRefreshListener = () => {
  // Check for token refresh every 30 minutes
  setInterval(async () => {
    try {
      if (!(await hasActiveAuthenticatedSession())) {
        return;
      }

      if (Notification.permission !== 'granted') return;

      const freshToken = await getFcmTokenBestEffort();

      if (!freshToken) return;

      const cached = await Preferences.get({ key: 'fcm_token' });
      if (cached.value !== freshToken) {
        console.log('FCM token refreshed, updating cache');
        await cacheFcmToken(freshToken);
      }
    } catch (err) {
      console.warn('Token refresh check failed:', err);
    }
  }, 30 * 60 * 1000);
};